import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { gql, GraphQLClient } from "graphql-request";

const LOGIN_OR_REGISTER_MUTATION = gql`
  mutation LoginOrRegister($input: LoginOrRegisterInput!) {
    loginOrRegister(input: $input) {
      boolean
    }
  }
`;

const GET_TOKEN_MUTATION = gql`
  mutation GetToken($input: TokenInput!) {
    token(input: $input) {
      authToken {
        token
        refreshToken
      }
    }
  }
`;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  loginOrRegister: (email: string) => Promise<boolean>;
  verifyCode: (code: string) => Promise<boolean>;
  logout: () => void;
  pendingEmail: string | null;
  /** Signed-in user's email from `userSync` — for the admin chip only, not a gate. */
  email: string | null;
  /** Signed-in user's display name from `userSync` — for the admin chip only. */
  name: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const GRAPHQL_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:5001/api/graphql";

const GRAPHQL_ANON_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_ANONYMOUS_ENDPOINT ||
  "http://localhost:5001/api/graphql-anonymous";

const AUTH_COOKIE_NAME = "gg_refresh_token";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Just the fields the admin chip needs — same `userSync` mutation as the payment app.
const USER_SYNC_MUTATION = gql`
  mutation UserSync {
    userSync {
      user {
        email
        fullName
      }
    }
  }
`;

function getAnonClient(): GraphQLClient {
  return new GraphQLClient(GRAPHQL_ANON_ENDPOINT, {
    headers: { "Content-Type": "application/json" },
  });
}

function getAuthClient(token: string): GraphQLClient {
  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

interface TokenResponse {
  token: { authToken: { token: string; refreshToken: string } };
}

interface UserSyncResponse {
  userSync: { user: { email: string | null; fullName: string | null } };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  const [authName, setAuthName] = useState<string | null>(null);

  // Fetch the signed-in user's profile for the admin chip — same `userSync` as the payment app.
  // Best-effort: the gate is the JWT `admin` role, so a failure here must NOT block login.
  const syncUser = useCallback(async (token: string) => {
    try {
      const data = await getAuthClient(token).request<UserSyncResponse>(USER_SYNC_MUTATION);
      setAuthEmail(data.userSync.user.email);
      setAuthName(data.userSync.user.fullName);
    } catch {
      // ignore — the chip just falls back to "Admin"
    }
  }, []);

  const applyToken = useCallback(
    (token: string, refresh: string) => {
      setAccessToken(token);
      setRefreshToken(refresh);
      document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(refresh)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Strict`;
      void syncUser(token);
    },
    [syncUser],
  );

  const loginOrRegister = useCallback(async (email: string): Promise<boolean> => {
    const client = getAnonClient();
    const data = await client.request<{ loginOrRegister: { boolean: boolean } }>(
      LOGIN_OR_REGISTER_MUTATION,
      { input: { email } },
    );
    const success = data.loginOrRegister?.boolean ?? false;
    if (success) setPendingEmail(email);
    return success;
  }, []);

  const verifyCode = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        const client = getAnonClient();
        const data = await client.request<TokenResponse>(GET_TOKEN_MUTATION, {
          input: { input: { code } },
        });
        const { token, refreshToken: rt } = data.token.authToken;
        applyToken(token, rt);
        setPendingEmail(null);
        return true;
      } catch {
        return false;
      }
    },
    [applyToken],
  );

  const loginWithRefreshToken = useCallback(
    async (rt: string): Promise<boolean> => {
      try {
        const client = getAnonClient();
        const data = await client.request<TokenResponse>(GET_TOKEN_MUTATION, {
          input: { input: { refreshToken: rt } },
        });
        const { token, refreshToken: newRt } = data.token.authToken;
        applyToken(token, newRt);
        return true;
      } catch {
        return false;
      }
    },
    [applyToken],
  );

  // Restore a session on mount from the refresh-token cookie.
  useEffect(() => {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${AUTH_COOKIE_NAME}=`));
    if (!match) return;
    const cookieRt = decodeURIComponent(match.split("=")[1]);
    setIsLoading(true);
    loginWithRefreshToken(cookieRt).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setPendingEmail(null);
    setAuthEmail(null);
    setAuthName(null);
    document.cookie = `${AUTH_COOKIE_NAME}=; max-age=0; path=/`;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        isAuthenticated: !!accessToken,
        isLoading,
        loginOrRegister,
        verifyCode,
        logout,
        pendingEmail,
        email: authEmail,
        name: authName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
};
